/* eslint-disable react-hooks/exhaustive-deps */
import OrderType from './OrderType';
import AdditionalInformation from './AdditionalInformation';
import DailyOrder from './DailyOrder';
import CardListInformation from './CardListInformation';
import CustomStickyBar from '@src/components/CustomStickyBar';
import MonthlyOrder from './MonthlyOrder';
import { Button, Form } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { confirmDialog, successDialog, Text, useText, warningDialog } from '@src/components';
import '@src/styles/cardOrder/cardInformation.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadBalancesDelete,
  loadBalancesUpdate,
  setTemplateStep,
  setOrderStep,
  setRunTimeTotalPrice,
  putLoadBalanceSubType,
  setSelectedRowKeys,
  setIsAllSelectedRowKeys,
  setExcelNoError,
  setIsMaxValuePopupOpen,
} from '@src/store/slice/balanceUploadSlice';
import {
  setBalanceUploadClear,
  resetBalanceFilterItems,
} from '@src/store/slice/balanceUploadDetailSlice';
import ExcelUpload from './ExcelUpload';
import { useHistory } from 'react-router-dom';
import { setDraftType } from '@src/store/slice/draftOrderSlice';
import { getAppSettings, setLoadingState } from '@src/store/slice/appSettingsSlice';
import { getMaximumLoadedAmount } from '@src/store/slice/orderLimitsSlice';
import { Wait } from '@src/utils/utils';

const BalanceInformation = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { currentLoadBalance, activeStep, excelLoadBalanceList, runTimeTotalPrice } = useSelector(
    (state) => state?.balanceUpload,
  );
  const { isShowPopup, ...currentLoadBalanceWithoutIsShowPopup } = currentLoadBalance;
  const { excelUploadErrorMessages, excelUploadStatus } = useSelector(
    (state) => state?.balanceUploadDetail,
  );
  const dispatch = useDispatch();
  const [isExit, setIsExit] = useState(false);
  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const orderContinueInstruction = useText('order.continueInstruction');
  const orderEnterBalanceForAtLeastOneCard = useText('order.enterBalanceForAtLeastOneCard');

  useEffect(async () => {
    await dispatch(getAppSettings({ code: 'MaxDailyUpload' }));
    await dispatch(getAppSettings({ code: 'MaxDailyAmountWithoutTax' }));
    await dispatch(getMaximumLoadedAmount());
  }, [dispatch]);

  const isDisabled = useMemo(() => {
    if (excelLoadBalanceList.orderStep === 3) {
      return excelUploadErrorMessages?.length > 0 || !excelUploadStatus;
    }
    return !(
      currentLoadBalance?.totalCount > 0 ||
      runTimeTotalPrice > 0 ||
      currentLoadBalance?.totalPrice > 0
    );
  }, [currentLoadBalance, runTimeTotalPrice, excelLoadBalanceList, excelUploadErrorMessages]);

  // Excel ile yüklemede hatalı satır kaldığı sürece (veya başarılı yükleme yokken) sipariş onaylanamaz.
  const isExcelApprovalDisabled = useMemo(() => {
    const isExcelOrder =
      excelLoadBalanceList?.orderStep === 3 || currentLoadBalance?.orderProcessType === 3;
    if (!isExcelOrder) {
      return false;
    }
    return excelUploadErrorMessages?.length > 0 || !excelUploadStatus;
  }, [
    excelLoadBalanceList?.orderStep,
    currentLoadBalance?.orderProcessType,
    excelUploadErrorMessages,
    excelUploadStatus,
  ]);

  const handleOrderApproval = useCallback(async () => {
    if (isExcelApprovalDisabled) {
      return;
    }
    setLoadingState(true);
    await Wait(2000);
    const additionalInformationValues = await form.validateFields();
    if (additionalInformationValues.activationDate === null && runTimeTotalPrice === 0) {
      warningDialog({
        title: <Text t="balanceApproval" />,
        message: orderContinueInstruction,
      });
      return Promise.resolve();
    }
    if (runTimeTotalPrice === 0) {
      warningDialog({
        title: <Text t="balanceApproval" />,
        message: orderEnterBalanceForAtLeastOneCard,
      });
      return;
    }
    const body = {
      ...currentLoadBalanceWithoutIsShowPopup,
      ...additionalInformationValues,
      activationDate: additionalInformationValues.activationDate,
      IsPreControl: true,
    };
    const response = await dispatch(loadBalancesUpdate(body));
    if (response.payload.data.isShowPopup) {
      dispatch(setIsMaxValuePopupOpen(true));
    } else {
      await dispatch(setIsMaxValuePopupOpen(false));
      const body = {
        ...currentLoadBalanceWithoutIsShowPopup,
        ...additionalInformationValues,
        activationDate: additionalInformationValues.activationDate,
        IsPreControl: false,
        orderStatus: 10,
      };
      await dispatch(loadBalancesUpdate(body));
      await dispatch(setRunTimeTotalPrice(0));
    }
    setLoadingState(false);
    const reportBody = {
      orderSubType: excelLoadBalanceList.orderStep ? excelLoadBalanceList.orderStep : 2,
      excelTemplateType:
        excelLoadBalanceList.orderStep === 3 ? excelLoadBalanceList.templateStep : null,
      orderId: currentLoadBalance?.id,
    };
    await dispatch(putLoadBalanceSubType(reportBody));
  }, [currentLoadBalance, form, dispatch, runTimeTotalPrice, isExcelApprovalDisabled]);

  const submitDraftSave = useCallback(
    async (to) => {
      const additionalInformationValues = await form.getFieldsValue();
      const body = {
        ...currentLoadBalance,
        ...additionalInformationValues,
        draft: true,
        orderStatus: 0,
        activationDate: additionalInformationValues.activationDate,
      };
      const action = await dispatch(loadBalancesUpdate(body));
      if (loadBalancesUpdate.fulfilled.match(action)) {
        successDialog({
          title: <Text t="successfullySent" />,
          message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
          onOk: () => {
            dispatch(setDraftType(2));
            history.push(to);
          },
        });
      } else {
        setIsExit(false);
      }
    },
    [currentLoadBalance, dispatch, form, history],
  );

  const handleOrderDraftSave = useCallback(async () => {
    confirmDialog({
      title: <Text t="attention" />,
      htmlContent: <Text t="orderDraftSaveConfirmMessage" />,
      onOk: async () => {
        setIsExit(true);
        await submitDraftSave('/ticket-restaurant/draft-order');
      },
    });
  }, [submitDraftSave]);

  const handleOrderCancel = useCallback(async () => {
    await dispatch(loadBalancesDelete());
  }, [dispatch, loadBalancesDelete]);

  const warningContent = <Text t="balanceUploadBeforeUnloadMessage" />;

  useEffect(() => {
    const unblock = history.block((location) => {
      if (isExit) {
        return true;
      }
      if (
        activeStep === 0 &&
        runTimeTotalPrice > 0 &&
        currentLoadBalance.pendingApproval !== true
      ) {
        confirmDialog({
          title: <Text t="attention" />,
          htmlContent: warningContent,
          okText: <Text t="yes" />,
          cancelText: <Text t="no" />,
          onOk: async () => {
            setIsExit(true);
            await submitDraftSave(location.pathname);
            await dispatch(setRunTimeTotalPrice(0));
            return true;
          },
          onCancel: async (e) => {
            if (e?.target?.className?.split(' ')?.includes('close-image')) return;
            if (currentLoadBalance?.id > 0) {
              const body = { id: currentLoadBalance?.id };
              await dispatch(loadBalancesDelete(body));
            }
            setIsExit(true);
            await dispatch(setRunTimeTotalPrice(0));
            history.push(location.pathname);
          },
        });

        unblock();
      } else {
        return true;
      }

      return false;
    });

    return unblock;
  }, [
    activeStep,
    runTimeTotalPrice,
    handleOrderCancel,
    history,
    submitDraftSave,
    isExit,
    dispatch,
    currentLoadBalance,
  ]);

  const callBackBalanceClear = useCallback(async () => {
    setTimeout(async () => {
      await dispatch(setBalanceUploadClear());
    }, 1250);
  }, [dispatch]);

  const isShowStickyBar = (currentLoadBalance) =>
    !(
      currentLoadBalance?.orderProcessType !== undefined &&
      currentLoadBalance?.orderProcessType !== 0
    );
  return (
    <div className="balance-upload-content">
      <OrderType />

      {currentLoadBalance.id !== 0 &&
        currentLoadBalance.periodType === 0 &&
        currentLoadBalance.orderProcessType !== 3 && <DailyOrder />}

      {currentLoadBalance.id !== 0 &&
        currentLoadBalance.periodType === 1 &&
        currentLoadBalance.orderProcessType !== 3 && <MonthlyOrder />}

      {currentLoadBalance.id !== 0 &&
        (currentLoadBalance?.orderProcessType !== 3 ||
          (currentLoadBalance?.orderProcessType === 3 &&
            excelUploadStatus &&
            excelUploadErrorMessages?.length === 0)) && <AdditionalInformation form={form} />}

      {currentLoadBalance?.orderProcessType === 3 && <ExcelUpload />}

      {currentLoadBalance.id !== 0 &&
        (currentLoadBalance?.orderProcessType !== 3 ||
          (currentLoadBalance?.orderProcessType === 3 &&
            currentLoadBalance?.totalCount > 0 &&
            excelLoadBalanceList?.isSuccess === true)) && <CardListInformation />}
      {isShowStickyBar(currentLoadBalance) ? null : (
        <CustomStickyBar>
          <div className="step-btn-container">
            <Button
              id="balanceUpload-balanceInformation-main-cancel-btn"
              className="cancel-btn"
              onClick={() => {
                return confirmDialog({
                  title: <Text t="attention" />,
                  htmlContent: <Text t="areYouSureYouWantToCancelThe" />,
                  okText: <Text t="yes" />,
                  cancelText: <Text t="no" />,
                  onOk: async () => {
                    await dispatch(setOrderStep(null));
                    await dispatch(setTemplateStep(0));
                    await dispatch(setSelectedRowKeys([]));
                    await dispatch(setIsAllSelectedRowKeys(false));
                    await dispatch(resetBalanceFilterItems());
                    await dispatch(setRunTimeTotalPrice(0));
                    await dispatch(setExcelNoError(false));
                    await handleOrderCancel();
                    await callBackBalanceClear();
                  },
                  onCancel: async () => { },
                });
              }}
            >
              <Text t="cancelOrder" />
            </Button>
            <Button
              id="balanceUpload-balanceInformation-main-draft-btn"
              className="draft-btn"
              onClick={handleOrderDraftSave}
              disabled={isDisabled}
            >
              <Text t="saveAsDraft" />
            </Button>
            <Button
              id="balanceUpload-balanceInformation-main-next-btn"
              className="next-btn"
              onClick={handleOrderApproval}
              disabled={isExcelApprovalDisabled}
            >
              <Text t="approveOrder" />
            </Button>
          </div>
        </CustomStickyBar>
      )}
    </div>
  );
};

export default BalanceInformation;
